// EP-111 T111-9 (D9/D11) — the "Add co-owner" / "Edit co-owner" modal behind
// the co-owner sidebar page. Renamed from CoOwnerFormModal by EP-133 SS4b-2
// (2026-08-20). A modal (not a `/shared-owners/:id/edit` route) by deliberate
// choice: Api::SharedOwnersController exposes only GET index + POST create +
// PATCH update today — no GET show — so a route requiring a fetch-by-id
// isn't buildable against the real backend yet. Edit pre-fills from the row
// already held by the list query, matching "mirror how Customers add/edit"
// in spirit while staying honest about the backend surface.
//
// Create mode collects unit_id (via a condominium-only property + unit
// picker) + the co-owner's person fields + usage_status. Edit mode omits the
// unit picker entirely (unit reassignment is out of scope — see the task
// description) and only touches person fields + usage_status.
//
// D15 (read-only-when-derived) — FIX-1: a co-owner's `usage_status` can be
// the string "rented" ONLY as a derived value (an active tenant RentalLease
// exists on the unit, see the backend's #effective_usage_status). The raw
// stored value is never "rented" (the column structurally rejects it). In
// edit mode, when the row's effective status is "rented" we hide the
// <select> entirely and render a read-only Thai label with a lock icon +
// "คำนวณจากสัญญาเช่า" explanation, so the operator can see the current
// status but cannot set it from this form. The PATCH payload in that case
// also omits `usage_status` so the operator's save doesn't silently send the
// raw stored value. Create mode never hits this branch (a freshly created
// co-owner has no associated tenant lease on the new unit at the moment of
// creation).
import { useEffect, useState } from 'react'
import { Lock } from 'lucide-react'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Modal, Button, Input, Select, SearchableDropdown, Badge } from '@/components/ui'
import { useAllProperties } from '@/hooks/useProperties'
import { usePropertyUnits, type PropertyUnit } from '@/hooks/useUnits'
import { useCreateSharedOwner, useUpdateSharedOwner } from '@/hooks/useOwnerContracts'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useNotificationStore } from '@/stores'
import { useLocalizationConfig } from '@/hooks/useDateFormatter'
import { localToday } from '@/lib/date'
import type { SharedOwnerLease } from '@/types'

export interface SharedOwnerFormModalProps {
  isOpen: boolean
  onClose: () => void
  /** Null → create mode (POST). Non-null → edit mode (PATCH), pre-filled from
   *  the list row already in hand — no separate fetch. */
  sharedOwner: SharedOwnerLease | null
  /** Fired after a successful create/update. */
  onSuccess: () => void
}

interface SharedOwnerFormData {
  unit_id: string
  first_name: string
  last_name: string
  phone: string
  email: string
  started_at: string
  usage_status: 'owner_occupied' | 'available_for_rent' | 'for_sale'
  // EP-111 amendment (payment info) — bank + PromptPay QR for routing the
  // CAF invoice directly to the co-owner. All optional; an empty form
  // submission writes {} to customers.payment_info.
  bank_name: string
  bank_account_number: string
  bank_account_holder: string
  promptpay_target: string
}

const USAGE_STATUS_OPTIONS: Array<SharedOwnerFormData['usage_status']> = [
  'owner_occupied',
  'available_for_rent',
  'for_sale',
]

export function SharedOwnerFormModal({ isOpen, onClose, sharedOwner, onSuccess }: SharedOwnerFormModalProps) {
  const { t } = useTranslation('shared_owners')
  const { t: tc } = useTranslation('common')
  const translateError = useApiErrorTranslator('shared_owners')
  const addToast = useNotificationStore((s) => s.addToast)
  const { timezone } = useLocalizationConfig()

  const isEdit = !!sharedOwner

  // D15 — mirror SharedOwnerCard's derivation: the value to DISPLAY is the
  // effective one (it overrides to 'rented' when an active tenant RentalLease
  // exists on the unit); the raw usage_status never itself carries 'rented'.
  // Only meaningful in edit mode — in create mode `sharedOwner` is null and
  // the Select always shows the 3 non-derived options (the freshly created
  // row can't have a derived 'rented' state at the moment of creation).
  const effectiveUsageStatus = sharedOwner
    ? (sharedOwner.effective_usage_status ?? sharedOwner.usage_status)
    : null
  const isRentedDerived = isEdit && effectiveUsageStatus === 'rented'

  const createSharedOwner = useCreateSharedOwner()
  const updateSharedOwner = useUpdateSharedOwner()

  // Condominium-only property + unit picker (create mode only — co-owners can
  // only be created for a `property_type: "condominium"` unit; the backend
  // 422s 'co_owner_requires_condominium_property' otherwise).
  const { data: allProperties } = useAllProperties(isOpen && !isEdit)
  const condoProperties = (allProperties ?? []).filter((p) => p.property_type === 'condominium')
  const [propertyId, setPropertyId] = useState<number | null>(null)

  // Unit picker (EP-116 T012): server search + page accumulation over
  // usePropertyUnits, replacing the old bare `usePropertyUnits(propertyId, 1)`
  // (page 1 only, no search — a property with >20 units silently truncated).
  // No eligibility filter is applied here, deliberately: unlike a lease's
  // vacant-unit picker, co-ownership is independent of tenancy (a unit can
  // carry a co-owner AND an active tenant lease at once — EP-111), and the
  // units endpoint itself has no co-owner-aware scoping. So this list is
  // every unit of the selected condo property, exactly as before — only the
  // paging/search mechanics change, not what's eligible.
  const [unitSearch, setUnitSearch] = useState('')
  const [unitPage, setUnitPage] = useState(1)
  const [accumulatedUnits, setAccumulatedUnits] = useState<PropertyUnit[]>([])
  const { data: unitsPage, isLoading: isLoadingUnits } = usePropertyUnits(
    propertyId ?? 0, unitPage, undefined, unitSearch || undefined,
  )

  // A new query (search OR property change) starts a new list; anything else
  // appends the next page. Also fires when the modal resets propertyId to
  // null on reopen (below), clearing a stale list from a previous session.
  useEffect(() => {
    setUnitPage(1)
    setAccumulatedUnits([])
  }, [unitSearch, propertyId])

  useEffect(() => {
    if (!unitsPage?.data) return
    setAccumulatedUnits((prev) => {
      if (unitPage === 1) return unitsPage.data
      const existingIds = new Set(prev.map((u) => u.id))
      return [...prev, ...unitsPage.data.filter((u) => !existingIds.has(u.id))]
    })
  }, [unitsPage, unitPage])

  const units = propertyId ? accumulatedUnits : []

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm<SharedOwnerFormData>({
    defaultValues: {
      unit_id: '',
      first_name: '',
      last_name: '',
      phone: '',
      email: '',
      started_at: localToday(timezone),
      usage_status: 'owner_occupied',
      bank_name: '',
      bank_account_number: '',
      bank_account_holder: '',
      promptpay_target: '',
    },
  })

  useEffect(() => {
    if (!isOpen) return
    if (sharedOwner) {
      const pi = sharedOwner.customer.payment_info ?? {}
      reset({
        unit_id: String(sharedOwner.unit_id),
        first_name: sharedOwner.customer.first_name ?? '',
        last_name: sharedOwner.customer.last_name ?? '',
        phone: sharedOwner.customer.phone ?? '',
        email: '',
        started_at: sharedOwner.started_at.split('T')[0],
        usage_status: (sharedOwner.usage_status as SharedOwnerFormData['usage_status']) ?? 'owner_occupied',
        bank_name: pi.bank_name ?? '',
        bank_account_number: pi.bank_account_number ?? '',
        bank_account_holder: pi.bank_account_holder ?? '',
        promptpay_target: pi.promptpay_target ?? '',
      })
      setPropertyId(null)
      // Reopening for a different co-owner must not carry over a stale
      // search from a previous session's unit picker.
      setUnitSearch('')
    } else {
      reset({
        unit_id: '',
        first_name: '',
        last_name: '',
        phone: '',
        email: '',
        started_at: localToday(timezone),
        usage_status: 'owner_occupied',
        bank_name: '',
        bank_account_number: '',
        bank_account_holder: '',
        promptpay_target: '',
      })
      setPropertyId(null)
      setUnitSearch('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, sharedOwner])

  const onSubmit = async (data: SharedOwnerFormData) => {
    const hasIdentifier = Boolean(data.first_name?.trim() || data.last_name?.trim() || data.phone?.trim() || data.email?.trim())
    if (!hasIdentifier) {
      addToast({ type: 'error', message: t('messages.identifier_required') })
      return
    }

    // Build payment_info from the four form fields. Empty fields are dropped
    // (not sent as empty strings) so partial updates don't clobber existing
    // values when only one of the four fields is touched in edit mode.
    const payment_info = {
      ...(data.bank_name?.trim() && { bank_name: data.bank_name.trim() }),
      ...(data.bank_account_number?.trim() && { bank_account_number: data.bank_account_number.trim() }),
      ...(data.bank_account_holder?.trim() && { bank_account_holder: data.bank_account_holder.trim() }),
      ...(data.promptpay_target?.trim() && { promptpay_target: data.promptpay_target.trim() }),
    }

    try {
      if (isEdit && sharedOwner) {
        // D15 read-only-when-derived — when the row's effective usage_status
        // is 'rented' the Select is hidden and the operator can't change it
        // from this form. Omit `usage_status` from the PATCH so we don't
        // silently re-send the raw value (the backend would 422 on a literal
        // 'rented', and re-sending the stored value would be a confusing
        // no-op). Other fields (name/phone/email/started_at/payment_info)
        // remain editable.
        await updateSharedOwner.mutateAsync({
          id: sharedOwner.id,
          first_name: data.first_name || undefined,
          last_name: data.last_name || undefined,
          phone: data.phone || undefined,
          email: data.email || undefined,
          started_at: data.started_at,
          ...(isRentedDerived ? {} : { usage_status: data.usage_status }),
          payment_info,
        })
        addToast({ type: 'success', message: t('messages.updated') })
      } else {
        if (!data.unit_id) {
          addToast({ type: 'error', message: t('messages.unit_required') })
          return
        }
        await createSharedOwner.mutateAsync({
          unit_id: Number(data.unit_id),
          first_name: data.first_name || undefined,
          last_name: data.last_name || undefined,
          phone: data.phone || undefined,
          email: data.email || undefined,
          started_at: data.started_at,
          usage_status: data.usage_status,
          payment_info,
        })
        addToast({ type: 'success', message: t('messages.created') })
      }
      onSuccess()
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  const isPending = createSharedOwner.isPending || updateSharedOwner.isPending

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? t('actions.edit') : t('actions.add_co_owner')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            {tc('actions.cancel')}
          </Button>
          <Button onClick={handleSubmit(onSubmit)} loading={isPending}>
            {isEdit ? t('actions.save_changes') : t('actions.create_co_owner')}
          </Button>
        </>
      }
    >
      <form className="space-y-4">
        {!isEdit && (
          <>
            <Select
              label={t('labels.property')}
              data-testid="co-owner-form-property"
              value={propertyId ? String(propertyId) : ''}
              onChange={(e) => {
                const newPropertyId = e.target.value ? Number(e.target.value) : null
                setPropertyId(newPropertyId)
                setValue('unit_id', '')
                // A unit search typed for the previous building must not
                // silently scope the new one.
                setUnitSearch('')
              }}
              options={[
                { value: '', label: '' },
                ...condoProperties.map((p) => ({ value: String(p.id), label: p.name })),
              ]}
            />
            <Controller
              name="unit_id"
              control={control}
              render={({ field, fieldState }) => (
                <SearchableDropdown
                  name="co-owner-form-unit"
                  label={t('labels.unit')}
                  error={fieldState.error?.message}
                  disabled={!propertyId}
                  value={field.value}
                  onChange={field.onChange}
                  onSearch={setUnitSearch}
                  isLoading={isLoadingUnits}
                  options={units.map((u) => ({ value: String(u.id), label: `${u.number} · ${u.name}` }))}
                  hasMore={
                    !!unitsPage?.meta &&
                    unitsPage.meta.current_page < unitsPage.meta.total_pages
                  }
                  onLoadMore={() => setUnitPage((p) => p + 1)}
                />
              )}
            />
          </>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <Input
            label={t('labels.first_name')}
            placeholder={t('placeholders.first_name')}
            error={errors.first_name?.message}
            {...register('first_name')}
          />
          <Input
            label={t('labels.last_name')}
            placeholder={t('placeholders.last_name')}
            error={errors.last_name?.message}
            {...register('last_name')}
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Input
            label={t('labels.phone')}
            placeholder={t('placeholders.phone')}
            error={errors.phone?.message}
            {...register('phone')}
          />
          <Input
            label={t('labels.email')}
            type="email"
            placeholder={t('placeholders.email')}
            error={errors.email?.message}
            {...register('email')}
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Input
            label={t('labels.started_at')}
            type="date"
            error={errors.started_at?.message}
            {...register('started_at')}
          />
          {/* D15 read-only-when-derived: when the row's effective usage_status
              is 'rented' (a tenant RentalLease is active on the unit), the
              operator cannot change status from this form — it's derived
              server-side from the lease. Render a read-only label with a
              lock icon + "คำนวณจากสัญญาเช่า" explanation instead of the
              <Select>. The full D15 derivation is documented in the file
              header. */}
          {isRentedDerived ? (
            <div data-testid="co-owner-form-usage-status-derived" className="form-control w-full">
              <label className="label" htmlFor="co-owner-form-usage-status-derived">
                <span className="label-text">{t('labels.usage_status')}</span>
              </label>
              {/* Read-only badge + explanation; the `cursor-not-allowed` +
                  `select-none` block visually communicates "this is not
                  editable" without taking focus or competing with the
                  other form controls. The onSubmit handler also strips
                  `usage_status` from the PATCH payload in this branch, so
                  RHF never needs a real <input> bound to that name. */}
              <div
                id="co-owner-form-usage-status-derived"
                className="flex items-center gap-2 px-3 py-2 border border-base-300 rounded-md bg-base-200/40 cursor-not-allowed select-none"
                aria-readonly="true"
                data-readonly="true"
              >
                <Lock className="w-4 h-4 shrink-0 text-base-content/50" aria-hidden="true" />
                <Badge variant="success">{t('status.rented')}</Badge>
                <span className="text-sm text-base-content/70 truncate">
                  {t('derived_status.explanation')}
                </span>
              </div>
            </div>
          ) : (
            <Select
              label={t('labels.usage_status')}
              data-testid="co-owner-form-usage-status"
              options={USAGE_STATUS_OPTIONS.map((v) => ({ value: v, label: t(`status.${v}`) }))}
              {...register('usage_status')}
            />
          )}
        </div>

        {/* EP-111 amendment (payment info) — bank + PromptPay QR for routing
            the CAF invoice directly to the co-owner. Optional fields; an
            empty submission writes {} to customers.payment_info. */}
        <div className="border-t pt-4">
          <h3 className="text-sm font-medium mb-3">{t('labels.payment_info')}</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label={t('labels.bank_name')}
              placeholder={t('placeholders.bank_name')}
              {...register('bank_name')}
            />
            <Input
              label={t('labels.bank_account_number')}
              placeholder={t('placeholders.bank_account_number')}
              {...register('bank_account_number')}
            />
            <Input
              label={t('labels.bank_account_holder')}
              placeholder={t('placeholders.bank_account_holder')}
              {...register('bank_account_holder')}
            />
            <Input
              label={t('labels.promptpay_target')}
              placeholder={t('placeholders.promptpay_target')}
              {...register('promptpay_target')}
            />
          </div>
        </div>
      </form>
    </Modal>
  )
}
